import React from 'react';
import { Skel } from './Skeleton';

// While the index list is on its way: its rows as shapes (Apal 2026-11-03:
// "anything that's loading must give loading effect") — never a blank box.
export const ChipRowsSkeleton = () => (
  <div className="space-y-2" data-testid="index-chips-pending">
    {[6, 4, 2, 4, 2].map((n, r) => (
      <div key={r} className="flex items-center gap-1.5">
        <span className="w-16 shrink-0"><Skel className="h-2.5 w-10" /></span>
        {Array.from({ length: n }, (_, i) => <Skel key={i} className="h-[26px] w-20" />)}
      </div>
    ))}
  </div>
);

/**
 * IndexChipRows — the ONE index-selection chip grid, shared by
 * Compare Indexes, Signal Analysis and the Allocation lab so the
 * three pages can never drift apart visually.
 *
 * Layout per row:  REGION ✓ | chip ✓ [L+S] | chip ✓ | …
 * - Region label is a toggle when onRegionToggle is provided, else a plain label.
 * - Selected chips show a trailing ✓ and, when canShort(sym), grow an attached
 *   L / L+S split button (violet = long+short book, teal = long-only).
 * - The region column is w-16 + nowrap so "CRYPTO ✓" never wraps to a second line.
 */
const IndexChipRows = ({
  regions,            // [{ code, name?, symbols: [{ name, label }] }]
  selected,           // string[] of selected symbol names
  onToggle,           // (name) => void
  onRegionToggle,     // optional (code) => void — omit to render label-only regions
  canShort,           // optional (name) => bool — show the L/L+S split button
  modeFor,            // optional (name) => 'ls' | 'l'
  onModeToggle,       // optional (name) => void
  isDisabled,         // optional (name) => bool — e.g. compare cap reached
  statusFor,          // optional (name) => null | { tag, title } — a selected index's state
                      // for the page's current period, e.g. "no data" / "in cash"
  loading = false,    // the list is still loading: shapes, not an empty box
}) => (loading && !regions.length) ? <ChipRowsSkeleton /> : (
  <div className="space-y-2">
    {regions.map(r => {
      const regionNames = r.symbols.map(s => s.name);
      const allSelected = regionNames.length > 0 && regionNames.every(n => selected.includes(n));
      const someSelected = regionNames.some(n => selected.includes(n));
      const regionCls = `text-[10px] font-bold uppercase tracking-wider w-16 shrink-0 whitespace-nowrap text-left ${
        onRegionToggle
          ? `transition-colors ${allSelected ? 'text-teal-400' : someSelected ? 'text-primary-400' : 'text-primary-400/60 hover:text-primary-400'}`
          : 'text-primary-400'
      }`;
      return (
        <div key={r.code} className="flex items-center gap-1.5 flex-wrap">
          {onRegionToggle ? (
            <button onClick={() => onRegionToggle(r.code)} className={regionCls}
              title={allSelected ? `Deselect all ${r.name || r.code}` : `Select all ${r.name || r.code}`}>
              {r.code}{allSelected ? ' ✓' : ''}
            </button>
          ) : (
            <span className={regionCls}>{r.code}</span>
          )}
          {r.symbols.map(s => {
            const sel = selected.includes(s.name);
            const dis = !sel && isDisabled ? isDisabled(s.name) : false;
            const short = sel && canShort ? canShort(s.name) : false;
            const status = sel && statusFor ? statusFor(s.name) : null;
            return (
              <span key={s.name} className="inline-flex items-center">
                <button onClick={() => !dis && onToggle(s.name)} disabled={dis}
                  title={status?.title}
                  data-status={status?.tag}
                  className={`px-2.5 py-1 text-xs font-medium transition-all ${
                    sel && status ? 'bg-teal-900/70 text-teal-100/80 shadow-sm'
                      : sel ? 'bg-teal-600 text-white shadow-sm'
                      : dis ? 'bg-dark-800 text-dark-600 cursor-not-allowed'
                        : 'bg-dark-700 text-dark-300 hover:bg-dark-600 hover:text-white'
                  } ${short ? 'rounded-l-md rounded-r-none' : 'rounded-md'}`}>
                  {s.label}{sel && ' ✓'}
                  {status && (
                    <span className="ml-1.5 px-1 py-px rounded text-[9px] font-bold uppercase tracking-wide bg-dark-900/60 text-amber-200/90">{status.tag}</span>
                  )}
                </button>
                {short && (
                  <button onClick={() => onModeToggle && onModeToggle(s.name)}
                    className={`px-1.5 py-1 rounded-r-md text-[9px] font-bold border-l border-dark-600 transition-all ${
                      modeFor && modeFor(s.name) === 'ls' ? 'bg-violet-600 text-white' : 'bg-teal-700 text-teal-200'
                    }`}>
                    {modeFor && modeFor(s.name) === 'ls' ? 'L+S' : 'L'}
                  </button>
                )}
              </span>
            );
          })}
        </div>
      );
    })}
  </div>
);

export default IndexChipRows;
